import type { NavConfigEntry, NavItem, NavSection } from '@zag/ui';

export interface PermissionAwareNavItem extends NavItem {
  requiredAnyPermissions?: string[];
}

export interface PermissionAwareNavSection extends NavSection {
  items: PermissionAwareNavItem[];
}

export type PermissionAwareNavConfigEntry = PermissionAwareNavItem | PermissionAwareNavSection;

/**
 * Control UI Navigation Configuration
 *
 * Defines the sidebar navigation for the control plane.
 * Environment-scoped items appear as primary navigation.
 * Admin items (RBAC, users) are in a separate section.
 *
 * Routes are parameterized with `:slug` for the active environment.
 * The layout component replaces `:slug` with the actual environment slug
 * before passing entries to the sidebar.
 */

/** Navigation entries shown when an environment is active */
export const ENV_NAV_CONFIG: PermissionAwareNavConfigEntry[] = [
  {
    id: 'dashboard',
    label: 'control.nav.dashboard',
    icon: 'dashboard',
    route: '/environments/:slug/dashboard',
    requiredAnyPermissions: ['environments:read', 'tenants:read'],
  },
  {
    id: 'tenants',
    label: 'control.nav.tenants',
    icon: 'dns',
    route: '/environments/:slug/tenants',
    requiredAnyPermissions: ['environments:read', 'tenants:read'],
  },
  {
    id: 'env-settings',
    label: 'control.nav.envSettings',
    icon: 'tune',
    route: '/environments/:slug/settings',
    // Accessible to anyone who can manage environment settings OR access grants
    requiredAnyPermissions: ['environments:update', 'environments:grants:read', 'environments:grants:update'],
  },
];

/** Navigation entries that are cross-environment (always visible) */
export const ADMIN_NAV_CONFIG: PermissionAwareNavConfigEntry[] = [
  {
    label: 'control.nav.sectionAdmin',
    items: [
      {
        id: 'admin-users',
        label: 'control.nav.users',
        icon: 'people',
        route: '/admin/users',
        requiredAnyPermissions: ['rbac:users:read', 'rbac:users:update'],
      },
      {
        id: 'admin-roles',
        label: 'control.nav.roles',
        icon: 'admin_panel_settings',
        route: '/admin/roles',
        requiredAnyPermissions: ['rbac:roles:read', 'rbac:roles:update'],
      },
      {
        id: 'admin-groups',
        label: 'control.nav.groups',
        icon: 'group_work',
        route: '/admin/groups',
        requiredAnyPermissions: ['rbac:groups:read', 'rbac:groups:update'],
      },
      {
        id: 'admin-permissions',
        label: 'control.nav.permissions',
        icon: 'security',
        route: '/admin/permissions',
        requiredAnyPermissions: ['rbac:permissions:read'],
      },
    ],
  },
  {
    label: 'control.nav.sectionLicensing',
    items: [
      {
        id: 'licensing-definitions',
        label: 'control.nav.licenceDefinitions',
        icon: 'card_membership',
        route: '/licensing/definitions',
        requiredAnyPermissions: ['licensing:definitions:read', 'licensing:definitions:manage'],
      },
    ],
  },
  {
    label: 'control.nav.sectionSettings',
    items: [
      {
        id: 'admin-authentication',
        label: 'control.nav.authentication',
        icon: 'lock',
        route: '/admin/authentication',
        requiredAnyPermissions: ['settings:auth:read', 'settings:auth:update'],
      },
      {
        id: 'admin-email',
        label: 'control.nav.email',
        icon: 'email',
        route: '/admin/email',
        requiredAnyPermissions: ['settings:smtp:read', 'settings:smtp:update'],
      },
      {
        id: 'admin-ai',
        label: 'control.nav.ai',
        icon: 'smart_toy',
        route: '/admin/ai',
        requiredAnyPermissions: ['settings:ai:read', 'settings:ai:update'],
      },
    ],
  },
];

export function toNavConfigEntries(entries: PermissionAwareNavConfigEntry[]): NavConfigEntry[] {
  return entries;
}
